import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { v4 as uuidv4 } from 'uuid';
import { Formik, Form as FormikForm, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import { useParams } from 'react-router-dom';

import useSession from 'core/hooks/use-session';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { track } from 'core/helpers/analytics';
import withDatePicker from 'core/components/with-date-picker';
import { FormField } from 'core/components/form';
import { useToast } from 'core/components/toast';
import { validUSState } from 'common/helpers/yup-helpers';
import useStyles from '../add-form.styles';

Yup.addMethod(Yup.mixed, 'validUSState', validUSState);

const schema = Yup.object().shape({
  nri: Yup.object().shape({
    firstName: Yup.string().required('First Name is required!'),
    lastName: Yup.string().required('Last Name is required'),
    dateOfBirth: Yup.string().required('Date of birth is required!'),
    removedReason: Yup.string().ensure().required('Reason is required!'),
    driversLicenseNumber: Yup.string().nullable(),
    driversLicenseState: Yup.string().validUSState('driversLicenseState').nullable()
  })
});

function AddForm({ onAdd, label, disabled }) {
  const classes = useStyles();
  const { values } = useFormikContext();
  const { drivers } = values;
  const toast = useToast();
  const { isAgency } = useSession();
  const { inPolicyRoute } = useRouteHelper();
  const params = useParams();

  return (
    <Formik
      initialValues={{
        nri: {
          id: uuidv4(),
          firstName: '',
          lastName: '',
          driversLicenseOrIDNumber: '',
          driversLicenseState: '',
          dateOfBirth: new Date().toISOString(),
          removedReason: null
        }
      }}
      validationSchema={schema}
      onSubmit={async ({ nri }) => {
        const personToAdd = {
          ...nri,
          driversLicenseNumber: nri.driversLicenseNumber?.trim(),
          firstName: nri.firstName?.trim().toUpperCase(),
          lastName: nri.lastName?.trim().toUpperCase()
        };
        const personAlreadyInDriversList = drivers?.some(
          (d) =>
            d.firstName?.trim().toUpperCase() === personToAdd.firstName &&
            d.lastName?.trim().toUpperCase() === personToAdd.lastName &&
            d.dateOfBirth === nri.dateOfBirth
        );
        if (personAlreadyInDriversList) {
          toast.notify({
            type: 'error',
            message:
              'A person with this name & date of birth is already a driver on the policy. Remove them as a driver to add them as a non-rated individual.'
          });
          return;
        }

        await track('Non-rated individual added', {
          id: inPolicyRoute ? params.policyId : params.offerId,
          isAgency,
          person: personToAdd
        });

        onAdd(personToAdd);
      }}
    >
      <FormikForm className={classes.container}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={4}>
            <FormField
              id="nri.firstName"
              name="nri.firstName"
              type="string"
              label="First Name"
              mode="light"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField
              id="nri.lastName"
              name="nri.lastName"
              type="string"
              label="Last Name"
              mode="light"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField
              id="nri.dateOfBirth"
              name="nri.dateOfBirth"
              type="date"
              label="Date of Birth"
              mode="light"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <FormField
              id="nri.removedReason"
              name="nri.removedReason"
              type="select"
              label="Reason"
              mode="light"
              disabled={disabled}
              options={lookupsJson?.removedDriverReasonOptions}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField
              id="nri.driversLicenseState"
              name="nri.driversLicenseState"
              type="state"
              label="License/ID State"
              mode="light"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField
              id="nri.driversLicenseNumber"
              name="nri.driversLicenseNumber"
              type="string"
              label="License/ID Number"
              mode="light"
              disabled={disabled}
            />
          </Grid>

          <Grid item xs={4} alignItems="end">
            <Button key="addButton" type="submit" variant="contained" color="secondary" disabled={disabled}>
              {label || 'Add People'}
            </Button>
          </Grid>
        </Grid>
      </FormikForm>
    </Formik>
  );
}

AddForm.propTypes = {
  onAdd: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

AddForm.defaultProps = {
  disabled: false
};

export default withDatePicker(AddForm);
